import { z } from 'zod';

/**
 * Module D · Appropriations + Demonstrativo Gerencial.
 *
 * An Appropriation explicitly declares that a real cost (from a PO,
 * receipt, payment, or manual memo) belongs to a specific budget_item.
 * The Demonstrativo merges existing PO-driven actuals (v_budget_item_actuals)
 * with appropriations from non-PO sources, never double-counting POs.
 */

export const appropriationSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  budgetItemId: z.string(),
  budgetItemDescription: z.string(),
  budgetItemCategory: z.string(),
  sourcePoId: z.string().nullable(),
  sourceReceiptId: z.string().nullable(),
  sourcePaymentId: z.string().nullable(),
  sourceLabel: z.string(),
  referenceDate: z.string(),
  costCents: z.number().int().positive(),
  description: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
});
export type Appropriation = z.infer<typeof appropriationSchema>;

export const createAppropriationRequestSchema = z.object({
  projectId: z.string().min(1),
  budgetItemId: z.string().min(1),
  sourcePoId: z.string().nullable().optional(),
  sourceReceiptId: z.string().nullable().optional(),
  sourcePaymentId: z.string().nullable().optional(),
  referenceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  costCents: z.number().int().positive(),
  description: z.string().max(300).nullable().optional(),
});
export type CreateAppropriationRequest = z.infer<typeof createAppropriationRequestSchema>;

export const demonstrativoRowSchema = z.object({
  budgetItemId: z.string(),
  description: z.string(),
  category: z.string(),
  plannedCents: z.number().int(),
  poActualCents: z.number().int(),
  appropriatedCents: z.number().int(),
  totalActualCents: z.number().int(),
  varianceCents: z.number().int(),
  variancePct: z.number(),
  overBudget: z.boolean(),
});
export type DemonstrativoRow = z.infer<typeof demonstrativoRowSchema>;

export const demonstrativoSchema = z.object({
  projectId: z.string(),
  generatedAt: z.string(),
  rows: z.array(demonstrativoRowSchema),
  totalPlannedCents: z.number().int(),
  totalActualCents: z.number().int(),
  totalVarianceCents: z.number().int(),
  byCategory: z.array(z.object({
    category: z.string(),
    plannedCents: z.number().int(),
    actualCents: z.number().int(),
    varianceCents: z.number().int(),
  })),
});
export type Demonstrativo = z.infer<typeof demonstrativoSchema>;

export const appropriationSuggestionSourceSchema = z.object({
  table: z.enum(['purchase_orders', 'payments', 'daily_logs']),
  id: z.string(),
  evidence: z.string(), // human-readable description (e.g., "PO 01k..." or "Payment 2026-04-15")
});
export type AppropriationSuggestionSource = z.infer<typeof appropriationSuggestionSourceSchema>;

export const appropriationSuggestionSchema = z.object({
  quantity: z.number().int().positive(),
  unitCost: z.number().int().nonnegative(),
  description: z.string().max(300),
  sources: z.array(appropriationSuggestionSourceSchema),
});
export type AppropriationSuggestion = z.infer<typeof appropriationSuggestionSchema>;
